/**
 * CAMASA CPS — Helpers centralizados de formatação (data, moeda, percentual).
 *
 * REGRA TIMEZONE: strings "YYYY-MM-DD" puras (date-only) são parseadas como UTC
 * midnight por padrão pelo JS — isso vira D-1 no Brasil (UTC-3). Ancoramos
 * SEMPRE ao meio-dia local ("T12:00:00") para preservar a data digitada pelo
 * usuário independente de fuso horário.
 *
 * Esta é a ÚNICA fonte de verdade para formatação de data/moeda/percentual.
 * Qualquer cópia local em outros arquivos é considerada bug.
 */

/** Detecta se a string é "YYYY-MM-DD" puro (sem hora). */
const isDateOnly = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s));

/** Date a partir de ISO/dateStr com ancoragem ao meio-dia local em casos date-only. */
export const parseData = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  const d = new Date(isDateOnly(s) ? s + "T12:00:00" : s);
  return isNaN(d.getTime()) ? null : d;
};

/** Formata data → dd/mm/yyyy ou "—" se vazio/inválido. */
export const fmtData = (iso) => {
  const d = parseData(iso);
  return d ? d.toLocaleDateString("pt-BR") : "—";
};

/** Formata data + hora → dd/mm/yyyy hh:mm. */
export const fmtDataHora = (iso) => {
  const d = parseData(iso);
  if (!d) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/** Formata data curta + hora → dd/mês hh:mm (ex: 21/mai 14:30). */
export const fmtDataCurta = (iso) => {
  const d = parseData(iso);
  if (!d) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

/** Formata apenas hora → hh:mm. */
export const fmtHora = (iso) => {
  const d = parseData(iso);
  if (!d) return "—";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

/** Formata relativo: "há X min/h/d" ou data absoluta se >30d. */
export const fmtRelativo = (iso) => {
  const d = parseData(iso);
  if (!d) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "há poucos segundos";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 30) return `há ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString("pt-BR");
};

/** Formata valor numérico → "R$ 1.234,56". Aceita null/undefined → "—" opcional. */
export const fmtMoeda = (v, { fallback = "R$ 0,00" } = {}) => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(v);
  if (!isFinite(n)) return fallback;
  return n.toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 2,
  });
};

/** Versão de fmtMoeda que retorna "—" se valor for null/undefined (usada em painéis financeiros). */
export const fmtMoedaOuTraco = (v) => fmtMoeda(v, { fallback: "—" });

/** Formata percentual → "12,3%". v=0.123 → "12,3%" (se já estiver fracionário) ou v=12.3 → "12,3%" (se já em pp). */
export const fmtPct = (v, { fracao = false, casas = 1 } = {}) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  const pct = fracao ? n * 100 : n;
  return pct.toLocaleString("pt-BR", {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  }) + "%";
};
